import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";

import FooterModal from "../common/FooterModal";
import FormItem from "../common/FormItem";
import Button from "../common/Button";
import DangerButton from "../common/DangerButton";
import VerifyCouponRedemptionInput from "./VerifyCouponRedemptionInput";

import { ItemHelper } from "../helpers/item-helper";
import { MaskInputHelper } from "../helpers/mask-input-helper";

const RedeemCouponModal = ({
  show,
  value,
  locationCode,
  onSuccess,
  onCancel,
  seniorLayout = false,
  redeemBcCoupon,
  redeemBcCouponError,
  redeemBcCouponResult,
  clearRedeemBcCouponError,
  setLoadingCoupon,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.toUpperCase();

  const [idCard, setIdCard] = useState("");
  const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (show) {
      setIdCard("");
      setConfirmButtonDisabled(false);
      setLocalError("");
    }
  }, [show]);

  useEffect(() => {
    if (redeemBcCouponResult) {
      onSuccess();
      setLoadingCoupon?.(false);
    }
  }, [redeemBcCouponResult]);

  useEffect(() => {
    if (idCard && redeemBcCouponError) {
      clearRedeemBcCouponError();
    }
  }, [idCard]);

  useEffect(() => {
    if (redeemBcCouponError) {
      setConfirmButtonDisabled(false); //  ปลดล็อกปุ่มเมื่อ error
      setLoadingCoupon?.(false);
    }
  }, [redeemBcCouponError]);

  const couponName = useMemo(() => {
    return ItemHelper.getName(value, i18n, lang);
  }, [value, lang]);

  const formattedPointRedeem = useMemo(() => {
    return MaskInputHelper.formatNumber(value?.pointRedeem || 0);
  }, [value]);

  const validateInput = (val) => val.length === 4;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateInput(idCard)) {
      setLocalError(t("verify_coupon_redemption_error"));
      return;
    }

    setConfirmButtonDisabled(true);
    setLocalError("");
    clearRedeemBcCouponError(); //  ล้าง error ก่อนเรียก

    // **เริ่ม loading**
    setLoadingCoupon?.(true);

    await redeemBcCoupon({
      couponId: value?.id,
      idCard,
      locationCode,
    });
  };

  return (
    <FooterModal show={show}>
      <form
        onSubmit={handleSubmit}
        className="w-full px-10 xs:px-6 pt-7 pb-10 flex flex-col items-center gap-y-2"
        autoComplete="off"
      >
        <div className="w-full text-3xl font-semibold pb-2">
          {t("confirm_redemption")}
        </div>

        <div className="w-full h-18 py-5 bg-secondary-background-gray rounded-xl flex justify-center items-end gap-x-2 text-22 font-semibold">
          <span className="leading-8">{t("use")}</span>
          <span className="text-3xl">{formattedPointRedeem}</span>
          <span className="leading-8">{t("big_point")}</span>
        </div>

        <div
          className={`w-full font-normal ${
            seniorLayout ? "text-xl" : "text-lg"
          }`}
        >
          {couponName}
        </div>

        <div
          className={`w-full text-center my-2 font-semibold ${
            seniorLayout ? "text-xl" : "text-lg"
          }`}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t("verify_coupon_redemption"),
            }}
          />
          <FormItem label="" error={localError}>
            <VerifyCouponRedemptionInput
              value={idCard}
              onChange={(val) => {
                setIdCard(val);
                setLocalError("");
                clearRedeemBcCouponError();
              }}
              error={!!(localError || redeemBcCouponError)} // เพื่อให้ border แดง
            />
          </FormItem>
        </div>

        {(redeemBcCouponError || localError) && (
          <div
            className={`w-full font-normal text-danger text-center pb-5 ${
              seniorLayout ? "text-base" : "text-sm"
            }`}
            dangerouslySetInnerHTML={{
              __html: redeemBcCouponError || localError,
            }}
          />
        )}

        <div className="w-full flex gap-x-3">
          <Button
            type="button"
            className={`w-full h-13 font-medium bg-overlay-5 ${
              seniorLayout ? "text-xl" : "text-lg"
            }`}
            shape="round"
            onClick={onCancel}
          >
            {t("go_back")}
          </Button>
          <div className="w-full h-13">
            <DangerButton
              buttonName={t("confirm")}
              seniorLayout={seniorLayout}
              disabled={confirmButtonDisabled}
              type="submit"
            />
          </div>
        </div>
      </form>
    </FooterModal>
  );
};

export default RedeemCouponModal;
